import { RevampTable, TableCell } from '@design/components/table';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import {
  CompaniesTableData,
  CompanyTableRow,
} from '@terminal/views/theme-overview/types';
import { RankingMethodologySection } from '@terminal/widgets/knowledge-graph-widget/components/theme-exposure-tab/theme-exposure-table/ranking-methodology-section/RankingMethodologySection';

import styles from './CompaniesTable.module.scss';
import { CompaniesTableHeaderCell } from './CompaniesTableHeaderCell';
import { getAlignment, THEME_OVERVIEW_COMPANY_COLUMN_ID } from './utils';

export const CompaniesTable = ({
  columns,
  data,
}: {
  columns: ColumnDef<CompanyTableRow>[];
  data: CompaniesTableData;
}) => {
  const { setQueryParams } = useQueryParams();
  const parsedData = data.filter(d => !!d.tag);

  const table = useReactTable({
    data: parsedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    autoResetPageIndex: false,
  });
  const tableRows = table.getRowModel().rows;

  const displayAsString = (columnId: string) => {
    return [THEME_OVERVIEW_COMPANY_COLUMN_ID].includes(columnId);
  };

  return (
    <div className={styles.root}>
      <RevampTable data-testid="theme-overview-companies-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup, idx) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <CompaniesTableHeaderCell<CompanyTableRow>
                    key={header.id}
                    header={header}
                    headerGroupIdx={idx}
                    alignment={getAlignment(index)}
                    isHoverEnabled={false}
                  />
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {tableRows.map(row => {
            return (
              <tr
                key={row.id}
                onClick={() => {
                  setQueryParams({
                    [queryKeys.activeDrawer]: queryValues.companyToThemeDrawer,
                    [queryKeys.entityTag]: row.original.company,
                  });
                }}
              >
                {row.getVisibleCells().map(cell => {
                  return (
                    <TableCell
                      key={cell.id}
                      style={{ whiteSpace: 'normal' }}
                      isNumber={!displayAsString(cell.column.id)}
                      alignment={'left'}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            );
          })}
          <tr data-testid="footer-row">
            <TableCell
              className={styles.footerRow}
              colspan={table.getHeaderGroups()[0].headers.length}
              isNumber={false}
            >
              <RankingMethodologySection rankingType={'themeToCompany'} />
            </TableCell>
          </tr>
        </tbody>
      </RevampTable>
    </div>
  );
};
